import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Uploaded media is stored as a permanent reference instead of a signed URL,
 * because signed URLs expire and leave broken images on profiles and posts.
 *
 * Stored forms we resolve:
 *   storage://<bucket>/<path>            — new uploads (permanent)
 *   https://.../storage/v1/object/sign/<bucket>/<path>?token=... — old uploads (expired signature)
 * Anything else (public http links, data URLs) is returned unchanged.
 */

const SIGNED_URL_TTL = 60 * 60 * 24 * 7; // 7 days, refreshed on each render session

const cache = new Map<string, string>();

export function parseStorageRef(value: string): { bucket: string; path: string } | null {
  if (value.startsWith("storage://")) {
    const rest = value.slice("storage://".length);
    const slash = rest.indexOf("/");
    if (slash <= 0) return null;
    return { bucket: rest.slice(0, slash), path: rest.slice(slash + 1) };
  }
  const m = value.match(/\/storage\/v1\/object\/sign\/([^/]+)\/([^?]+)/);
  if (m) return { bucket: m[1], path: decodeURIComponent(m[2]) };
  return null;
}

export function storageRef(bucket: string, path: string) {
  return `storage://${bucket}/${path}`;
}

/** Resolve a stored media reference to a displayable URL. */
export async function resolveMediaUrl(value: string | null | undefined): Promise<string | null> {
  if (!value) return null;
  const ref = parseStorageRef(value);
  if (!ref) return value;
  const key = `${ref.bucket}/${ref.path}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const { data, error } = await supabase.storage.from(ref.bucket).createSignedUrl(ref.path, SIGNED_URL_TTL);
  if (error || !data?.signedUrl) return null;
  cache.set(key, data.signedUrl);
  return data.signedUrl;
}

/** React hook: resolves a stored media reference to a fresh signed URL. */
export function useMediaUrl(value: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() => {
    if (!value) return null;
    const ref = parseStorageRef(value);
    if (!ref) return value;
    return cache.get(`${ref.bucket}/${ref.path}`) ?? null;
  });

  useEffect(() => {
    let alive = true;
    if (!value) { setUrl(null); return; }
    const ref = parseStorageRef(value);
    if (!ref) { setUrl(value); return; }
    const key = `${ref.bucket}/${ref.path}`;
    const hit = cache.get(key);
    if (hit) { setUrl(hit); return; }
    resolveMediaUrl(value).then((u) => { if (alive) setUrl(u); });
    return () => { alive = false; };
  }, [value]);

  return url;
}

/** Resolve a list of stored media references. */
export async function resolveMediaUrls(values: string[]): Promise<string[]> {
  const out = await Promise.all(values.map((v) => resolveMediaUrl(v)));
  return out.filter((v): v is string => !!v);
}
